import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiTrash2, FiSend, FiRotateCcw, FiEye, FiLayers } from 'react-icons/fi';

const TABS = ['Chart of Accounts', 'Journals', 'Recurring Journals', 'Opening Balances', 'Fiscal Years', 'Trial Balance'];
const CATEGORY_OPTIONS = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'COS', 'EXPENSE', 'OTHER_INCOME', 'OTHER_EXPENSE'];
const emptyJournalLine = { accountId: '', debit: '', credit: '', description: '' };
const emptyRecurringLine = { accountId: '', debit: '', credit: '', description: '' };

const Accounting = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Chart of Accounts');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [trialBalance, setTrialBalance] = useState(null);
  const [recurringJournals, setRecurringJournals] = useState([]);
  const [openingBalances, setOpeningBalances] = useState([]);

  const [journalWorkflowId, setJournalWorkflowId] = useState(null);
  const [journalApprovalRequests, setJournalApprovalRequests] = useState([]);

  const [accountForm, setAccountForm] = useState({ code: '', name: '', categoryCode: 'EXPENSE' });
  const [journalForm, setJournalForm] = useState({ journalDate: new Date().toISOString().slice(0, 10), reference: '', description: '', lines: [{ ...emptyJournalLine }, { ...emptyJournalLine }] });
  const [fiscalYearForm, setFiscalYearForm] = useState({ preset: 'general', name: '', startDate: '', endDate: '' });
  const [recurringForm, setRecurringForm] = useState({ name: '', frequency: 'monthly', nextRunDate: '', endDate: '', lines: [{ ...emptyRecurringLine }, { ...emptyRecurringLine }] });
  const [openingForm, setOpeningForm] = useState({ accountId: '', fiscalYearId: '', amount: '' });

  const [selectedJournal, setSelectedJournal] = useState(null);
  const [journalLines, setJournalLines] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/accounting/accounts'),
      apiClient.get('/accounting/journals'),
      apiClient.get('/accounting/fiscal-years'),
      apiClient.get('/accounting/fiscal-years/periods'),
      apiClient.get('/workflow/workflows', { params: { appliesToType: 'journal' } }).catch(() => ({ data: { data: [] } })),
      apiClient.get('/workflow/approvals').catch(() => ({ data: { data: [] } })),
      apiClient.get('/accounting/recurring-journals').catch(() => ({ data: { data: [] } })),
      apiClient.get('/accounting/opening-balances').catch(() => ({ data: { data: [] } })),
    ]).then(([accRes, jnlRes, fyRes, perRes, wfRes, reqRes, recurRes, openBalRes]) => {
      setAccounts(accRes.data.data);
      setJournals(jnlRes.data.data);
      setFiscalYears(fyRes.data.data);
      setPeriods(perRes.data.data);
      const activeJournalWorkflow = wfRes.data.data.find((w) => w.isActive);
      setJournalWorkflowId(activeJournalWorkflow ? activeJournalWorkflow.id : null);
      setJournalApprovalRequests(reqRes.data.data.filter((r) => r.entityType === 'journal'));
      setRecurringJournals(recurRes.data.data);
      setOpeningBalances(openBalRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load accounting data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accountLabel = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} — ${a.name}` : id;
  };

  const approvalForJournal = (journalId) => journalApprovalRequests.find((r) => r.entityId === journalId);

  // ---- Chart of Accounts ----
  const handleSeedDefaults = async () => {
    try {
      const { data } = await apiClient.post('/accounting/accounts/seed-defaults');
      success(`✅ ${data.data.length} default accounts created`);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not seed default accounts')); }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/accounting/accounts', accountForm);
      success('✅ Account added');
      setShowForm(false);
      setAccountForm({ code: '', name: '', categoryCode: 'EXPENSE' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add account')); }
  };

  const handleToggleAccountActive = async (accountId, currentlyActive) => {
    try {
      await apiClient.patch(`/accounting/accounts/${accountId}`, { isActive: !currentlyActive });
      success(currentlyActive ? '✅ Account deactivated' : '✅ Account reactivated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update account')); }
  };

  // ---- Journals ----
  const updateJournalLine = (idx, field, value) => {
    setJournalForm((prev) => {
      const lines = [...prev.lines];
      if (field === 'debit') {
        lines[idx] = { ...lines[idx], debit: value, credit: value ? '' : lines[idx].credit };
      } else if (field === 'credit') {
        lines[idx] = { ...lines[idx], credit: value, debit: value ? '' : lines[idx].debit };
      } else {
        lines[idx] = { ...lines[idx], [field]: value };
      }
      return { ...prev, lines };
    });
  };
  const addJournalLine = () => setJournalForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyJournalLine }] }));
  const removeJournalLine = (idx) => setJournalForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const journalTotals = () => {
    const debit = journalForm.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
    const credit = journalForm.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
    return { debit: debit.toFixed(2), credit: credit.toFixed(2), balanced: Math.abs(debit - credit) < 0.005 };
  };
  const totals = journalTotals();

  const handleCreateJournal = async (e) => {
    e.preventDefault();
    if (!totals.balanced) {
      showError('Debits and credits must balance before this can be saved');
      return;
    }
    try {
      const payload = {
        ...journalForm,
        lines: journalForm.lines
          .filter((l) => l.accountId && (l.debit || l.credit))
          .map((l) => ({
            accountId: l.accountId,
            ...(l.debit ? { debit: l.debit } : {}),
            ...(l.credit ? { credit: l.credit } : {}),
            ...(l.description ? { description: l.description } : {}),
          })),
      };
      if (!payload.reference) delete payload.reference;
      await apiClient.post('/accounting/journals', payload);
      success('✅ Journal saved as draft');
      setShowForm(false);
      setJournalForm({ journalDate: new Date().toISOString().slice(0, 10), reference: '', description: '', lines: [{ ...emptyJournalLine }, { ...emptyJournalLine }] });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create journal')); }
  };

  const viewJournal = async (journal) => {
    setSelectedJournal(journal);
    setJournalLines(null);
    try {
      const { data } = await apiClient.get(`/accounting/journals/${journal.id}/lines`);
      setJournalLines(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load journal lines')); }
  };

  const handlePostJournal = async (journalId) => {
    try {
      await apiClient.post(`/accounting/journals/${journalId}/post`);
      success('✅ Journal posted');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not post journal')); }
  };

  const handleReverseJournal = async (journalId) => {
    if (!window.confirm('Reverse this journal? This posts an equal and opposite entry — it does not delete anything.')) return;
    try {
      await apiClient.post(`/accounting/journals/${journalId}/reverse`);
      success('✅ Journal reversed');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not reverse journal')); }
  };

  const handleSubmitJournalForApproval = async (journal) => {
    if (!journalWorkflowId) return;
    try {
      await apiClient.post('/workflow/approvals', {
        workflowId: journalWorkflowId,
        entityType: 'journal',
        entityId: journal.id,
        amount: journal.totalDebit,
      });
      success('✅ Submitted for approval');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not submit for approval')); }
  };

  // ---- Recurring Journals ----
  const updateRecurringLine = (idx, field, value) => {
    setRecurringForm((prev) => {
      const lines = [...prev.lines];
      if (field === 'debit') lines[idx] = { ...lines[idx], debit: value, credit: value ? '' : lines[idx].credit };
      else if (field === 'credit') lines[idx] = { ...lines[idx], credit: value, debit: value ? '' : lines[idx].debit };
      else lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, lines };
    });
  };
  const addRecurringLine = () => setRecurringForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyRecurringLine }] }));

  const handleCreateRecurring = async (e) => {
  e.preventDefault();
  try {
    const payload = { ...recurringForm };
    if (!payload.endDate) delete payload.endDate;
    payload.lines = recurringForm.lines
      .filter((l) => l.accountId && (l.debit || l.credit))
      .map((l) => ({
        accountId: l.accountId,
        ...(l.debit ? { debit: l.debit } : {}),
        ...(l.credit ? { credit: l.credit } : {}),
        ...(l.description ? { description: l.description } : {}),
      }));
    await apiClient.post('/accounting/recurring-journals', payload);
    success('✅ Recurring journal template created');
    setShowForm(false);
    setRecurringForm({ name: '', frequency: 'monthly', nextRunDate: '', endDate: '', lines: [{ ...emptyRecurringLine }, { ...emptyRecurringLine }] });
    loadAll();
  } catch (err) { showError(getErrorMessage(err, 'Could not create recurring journal')); }
  };

  const handleGenerateRecurring = async (templateId) => {
    try {
      await apiClient.post(`/accounting/recurring-journals/${templateId}/generate`, {});
      success('✅ Journal generated as draft — check the Journals tab');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not generate journal')); }
  };

  // ---- Opening Balances ----
  const handleSetOpeningBalance = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/accounting/opening-balances', openingForm);
      success('✅ Opening balance set');
      setShowForm(false);
      setOpeningForm({ accountId: '', fiscalYearId: '', amount: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not set opening balance')); }
  };

  // ---- Fiscal Years ----
  const applyPreset = (preset) => {
    const year = new Date().getFullYear();
    if (preset === 'general') {
      setFiscalYearForm({ preset, name: `FY${year}`, startDate: `${year}-01-01`, endDate: `${year}-12-31` });
    } else if (preset === 'uk') {
      setFiscalYearForm({ preset, name: `UK ${year}/${year + 1}`, startDate: `${year}-04-06`, endDate: `${year + 1}-04-05` });
    } else {
      setFiscalYearForm({ preset: 'custom', name: '', startDate: '', endDate: '' });
    }
  };

  const handleCreateFiscalYear = async (e) => {
    e.preventDefault();
    try {
      const { data } = await apiClient.post('/accounting/fiscal-years', {
        name: fiscalYearForm.name,
        startDate: fiscalYearForm.startDate,
        endDate: fiscalYearForm.endDate,
      });
      await apiClient.post(`/accounting/fiscal-years/${data.data.id}/generate-periods`);
      success('✅ Fiscal year created with 12 monthly periods');
      setShowForm(false);
      setFiscalYearForm({ preset: 'general', name: '', startDate: '', endDate: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create fiscal year')); }
  };

  const periodsForYear = (fiscalYearId) => periods.filter((p) => p.fiscalYearId === fiscalYearId);

  const handleLockPeriod = async (periodId) => {
    if (!window.confirm('Lock this period? No further journals can post into it once locked.')) return;
    try {
      await apiClient.post(`/accounting/fiscal-years/periods/${periodId}/lock`);
      success('✅ Period locked');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not lock period')); }
  };

  // ---- Trial Balance ----
  const loadTrialBalance = async () => {
    try {
      const { data } = await apiClient.get('/accounting/ledger/trial-balance');
      setTrialBalance(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load trial balance')); }
  };

  useEffect(() => {
    if (tab === 'Trial Balance' && trialBalance === null) loadTrialBalance();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Accounting</h1>
        <p>Chart of accounts, journals, fiscal periods, and trial balance</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedJournal(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Chart of Accounts' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Chart of Accounts</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {accounts.length === 0 && (
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handleSeedDefaults}>
                  <FiLayers size={14} /> Seed Defaults
                </button>
              )}
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Account'}
              </button>
            </div>
          </div>
          {showForm && (
            <form onSubmit={handleAddAccount} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Code *</label><input required value={accountForm.code} onChange={(e) => setAccountForm(p => ({ ...p, code: e.target.value }))} placeholder="6500" /></div>
              <div className="form-field"><label>Name *</label><input required value={accountForm.name} onChange={(e) => setAccountForm(p => ({ ...p, name: e.target.value }))} placeholder="Marketing Expenses" /></div>
              <div className="form-field">
                <label>Category *</label>
                <select required value={accountForm.categoryCode} onChange={(e) => setAccountForm(p => ({ ...p, categoryCode: e.target.value }))}>
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : accounts.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    No accounts yet — click "Seed Defaults" for a standard starting chart, or add your own.
                  </td></tr>
                ) : accounts.map((a) => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{a.code}</td>
                    <td>{a.name}</td>
                    <td style={{ textTransform: 'capitalize' }}>{a.categoryCode?.replace('_', ' ').toLowerCase()}</td>
                    <td><span className="badge" style={{ background: a.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{a.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleToggleAccountActive(a.id, a.isActive)}>
                        {a.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Journals' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>Journals</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Manual Journal'}
              </button>
            </div>
            {!journalWorkflowId && (
              <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1rem' }}>
                No active approval workflow is set up for journals yet — drafts can only be posted directly. Set one up under Workflow → Workflows (Applies To: Journal) to enable routed approval.
              </p>
            )}
            {showForm && (
              <form onSubmit={handleCreateJournal} style={{ marginBottom: '1.5rem' }}>
                <div className="form-grid">
                  <div className="form-field"><label>Date *</label><input type="date" required value={journalForm.journalDate} onChange={(e) => setJournalForm(p => ({ ...p, journalDate: e.target.value }))} /></div>
                  <div className="form-field"><label>Reference</label><input value={journalForm.reference} onChange={(e) => setJournalForm(p => ({ ...p, reference: e.target.value }))} /></div>
                  <div className="form-field full-width"><label>Description</label><input value={journalForm.description} onChange={(e) => setJournalForm(p => ({ ...p, description: e.target.value }))} /></div>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>Lines (must balance)</label>
                  {journalForm.lines.map((line, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                      <select required value={line.accountId} onChange={(e) => updateJournalLine(idx, 'accountId', e.target.value)} style={{ flex: 1, padding: '0.5rem' }}>
                        <option value="">-- account --</option>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                      </select>
                      <input placeholder="Description" value={line.description} onChange={(e) => updateJournalLine(idx, 'description', e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                      <input type="number" step="0.01" placeholder="Debit" value={line.debit} onChange={(e) => updateJournalLine(idx, 'debit', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                      <input type="number" step="0.01" placeholder="Credit" value={line.credit} onChange={(e) => updateJournalLine(idx, 'credit', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                      {journalForm.lines.length > 2 && (
                        <button type="button" className="action-icon delete" onClick={() => removeJournalLine(idx)}><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={addJournalLine}>
                    <FiPlus size={14} /> Add Line
                  </button>
                </div>
                <div style={{ textAlign: 'right', marginTop: '1rem', fontWeight: 700, color: totals.balanced ? '#10b981' : '#ef4444' }}>
                  Debit: {totals.debit} · Credit: {totals.credit} {totals.balanced ? '✓ Balanced' : '✗ Not balanced'}
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" style={{ width: 'auto' }} disabled={!totals.balanced}>Save Draft</button>
                </div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Date</th><th>Reference</th><th>Description</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {journals.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No journals yet</td></tr>
                  ) : journals.map((j) => {
                    const approval = approvalForJournal(j.id);
                    return (
                      <tr key={j.id}>
                        <td>{new Date(j.journalDate).toLocaleDateString()}</td>
                        <td>{j.reference || '—'}</td>
                        <td>{j.description || '—'}</td>
                        <td style={{ textTransform: 'capitalize' }}>{j.journalType}</td>
                        <td><span className="badge" style={{ background: j.status === 'posted' ? '#10b981' : j.status === 'reversed' ? '#ef4444' : '#f59e0b', color: 'white' }}>{j.status}</span></td>
                        <td>
                          <div className="actions" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                            <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewJournal(j)}>
                              <FiEye size={14} /> View
                            </button>
                            {approval && (
                              <span className="badge" style={{
                                background: approval.status === 'approved' ? '#10b981' : approval.status === 'rejected' ? '#ef4444' : approval.status === 'escalated' ? '#f59e0b' : '#3b82f6',
                                color: 'white',
                              }}>
                                Approval: {approval.status}
                              </span>
                            )}
                            {j.status === 'draft' && !approval && journalWorkflowId && (
                              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSubmitJournalForApproval(j)}>
                                Submit for Approval
                              </button>
                            )}
                            {j.status === 'draft' && (
                              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handlePostJournal(j.id)}>
                                <FiSend size={14} /> Post
                              </button>
                            )}
                            {j.status === 'posted' && (
                              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleReverseJournal(j.id)}>
                                <FiRotateCcw size={14} /> Reverse
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {selectedJournal && (
            <section className="page-section">
              <div className="section-header">
                <h2>Journal Lines — {selectedJournal.description || selectedJournal.reference || selectedJournal.id}</h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedJournal(null)}>Close</button>
              </div>
              {journalLines === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Account</th><th>Description</th><th>Debit</th><th>Credit</th></tr></thead>
                    <tbody>
                      {journalLines.map((l) => (
                        <tr key={l.id}>
                          <td>{accountLabel(l.accountId)}</td>
                          <td>{l.description || '—'}</td>
                          <td>{Number(l.debit) > 0 ? Number(l.debit).toFixed(2) : ''}</td>
                          <td>{Number(l.credit) > 0 ? Number(l.credit).toFixed(2) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'Recurring Journals' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Recurring Journal Templates</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Template'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleCreateRecurring} style={{ marginBottom: '1.5rem' }}>
              <div className="form-grid">
                <div className="form-field"><label>Name *</label><input required value={recurringForm.name} onChange={(e) => setRecurringForm(p => ({ ...p, name: e.target.value }))} placeholder="Monthly Rent" /></div>
                <div className="form-field">
                  <label>Frequency</label>
                  <select value={recurringForm.frequency} onChange={(e) => setRecurringForm(p => ({ ...p, frequency: e.target.value }))}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
                <div className="form-field"><label>Next Run Date *</label><input required type="date" value={recurringForm.nextRunDate} onChange={(e) => setRecurringForm(p => ({ ...p, nextRunDate: e.target.value }))} /></div>
                <div className="form-field"><label>End Date</label><input type="date" value={recurringForm.endDate} onChange={(e) => setRecurringForm(p => ({ ...p, endDate: e.target.value }))} /></div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>Lines (must balance)</label>
                {recurringForm.lines.map((line, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <select required value={line.accountId} onChange={(e) => updateRecurringLine(idx, 'accountId', e.target.value)} style={{ flex: 1, padding: '0.5rem' }}>
                      <option value="">-- account --</option>
                      {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                    </select>
                    <input placeholder="Description" value={line.description} onChange={(e) => updateRecurringLine(idx, 'description', e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                    <input type="number" step="0.01" placeholder="Debit" value={line.debit} onChange={(e) => updateRecurringLine(idx, 'debit', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                    <input type="number" step="0.01" placeholder="Credit" value={line.credit} onChange={(e) => updateRecurringLine(idx, 'credit', e.target.value)} style={{ width: '90px', padding: '0.5rem' }} />
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={addRecurringLine}>
                  <FiPlus size={14} /> Add Line
                </button>
              </div>
              <div className="form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Template</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Frequency</th><th>Next Run</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {recurringJournals.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No recurring templates yet</td></tr>
                ) : recurringJournals.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td style={{ textTransform: 'capitalize' }}>{r.frequency}</td>
                    <td>{new Date(r.nextRunDate).toLocaleDateString()}</td>
                    <td><span className="badge" style={{ background: r.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{r.isActive ? 'Yes' : 'No'}</span></td>
                    <td><button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleGenerateRecurring(r.id)}>Generate Now</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Opening Balances' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Opening Balances</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Set Opening Balance'}
            </button>
          </div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Starting balances for each account when you began using this system mid-year — these feed into the trial balance alongside posted journals.
          </p>
          {showForm && (
            <form onSubmit={handleSetOpeningBalance} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>Account *</label>
                <select required value={openingForm.accountId} onChange={(e) => setOpeningForm(p => ({ ...p, accountId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Fiscal Year *</label>
                <select required value={openingForm.fiscalYearId} onChange={(e) => setOpeningForm(p => ({ ...p, fiscalYearId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {fiscalYears.map((fy) => <option key={fy.id} value={fy.id}>{fy.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Amount *</label><input required type="number" step="0.01" value={openingForm.amount} onChange={(e) => setOpeningForm(p => ({ ...p, amount: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Set</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Account</th><th>Amount</th></tr></thead>
              <tbody>
                {openingBalances.length === 0 ? (
                  <tr><td colSpan={2} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No opening balances set yet</td></tr>
                ) : openingBalances.map((b) => (
                  <tr key={b.id}><td>{accountLabel(b.accountId)}</td><td style={{ fontWeight: 600 }}>{Number(b.amount).toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Fiscal Years' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Fiscal Years</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => { setShowForm((s) => !s); if (!showForm) applyPreset('general'); }}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Fiscal Year'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleCreateFiscalYear} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field full-width">
                <label>Type</label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.3rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', textTransform: 'none', fontWeight: 400 }}>
                    <input type="radio" name="preset" checked={fiscalYearForm.preset === 'general'} onChange={() => applyPreset('general')} style={{ width: 'auto' }} />
                    General (Jan – Dec)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', textTransform: 'none', fontWeight: 400 }}>
                    <input type="radio" name="preset" checked={fiscalYearForm.preset === 'uk'} onChange={() => applyPreset('uk')} style={{ width: 'auto' }} />
                    UK Tax Year (6 Apr – 5 Apr)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', textTransform: 'none', fontWeight: 400 }}>
                    <input type="radio" name="preset" checked={fiscalYearForm.preset === 'custom'} onChange={() => applyPreset('custom')} style={{ width: 'auto' }} />
                    Custom
                  </label>
                </div>
              </div>
              <div className="form-field"><label>Name *</label><input required value={fiscalYearForm.name} onChange={(e) => setFiscalYearForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-field"><label>Start Date *</label><input type="date" required value={fiscalYearForm.startDate} onChange={(e) => setFiscalYearForm(p => ({ ...p, startDate: e.target.value, preset: 'custom' }))} /></div>
              <div className="form-field"><label>End Date *</label><input type="date" required value={fiscalYearForm.endDate} onChange={(e) => setFiscalYearForm(p => ({ ...p, endDate: e.target.value, preset: 'custom' }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create (auto-generates 12 monthly periods)</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Period</th><th>Status</th></tr></thead>
              <tbody>
                {fiscalYears.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No fiscal years yet</td></tr>
                ) : fiscalYears.map((fy) => (
                  <React.Fragment key={fy.id}>
                    <tr>
                      <td style={{ fontWeight: 600 }}>{fy.name}</td>
                      <td>{new Date(fy.startDate).toLocaleDateString()} – {new Date(fy.endDate).toLocaleDateString()}</td>
                      <td><span className="badge" style={{ background: fy.status === 'closed' ? '#9ca3af' : '#10b981', color: 'white' }}>{fy.status || 'open'}</span></td>
                    </tr>
                    {periodsForYear(fy.id).length > 0 && (
                      <tr>
                        <td colSpan={3} style={{ background: '#f9fafb', padding: '0.75rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {periodsForYear(fy.id).map((p) => (
                              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.7rem', background: 'white', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '0.8rem' }}>
                                <span>{new Date(p.startDate).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })}</span>
                                {p.isLocked ? (
                                  <span style={{ color: '#9ca3af' }}>🔒</span>
                                ) : (
                                  <button className="btn btn-sm btn-outline" style={{ width: 'auto', padding: '0.1rem 0.4rem', fontSize: '0.75rem' }} onClick={() => handleLockPeriod(p.id)}>Lock</button>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Trial Balance' && (
        <section className="page-section">
          <div className="section-header"><h2>Trial Balance</h2></div>
          {trialBalance === null ? (
            <p style={{ color: '#6b7280' }}>Loading...</p>
          ) : (() => {
            const totalDebits = trialBalance.reduce((s, r) => s + Number(r.totalDebit), 0);
            const totalCredits = trialBalance.reduce((s, r) => s + Number(r.totalCredit), 0);
            const balanced = Math.abs(totalDebits - totalCredits) < 0.005;
            const activeRows = trialBalance.filter((r) => Number(r.totalDebit) > 0 || Number(r.totalCredit) > 0);
            return (
              <>
                <div className="stats-row">
                  <div className="stat-box"><div className="label">Total Debits</div><div className="value">{totalDebits.toFixed(2)}</div></div>
                  <div className="stat-box"><div className="label">Total Credits</div><div className="value">{totalCredits.toFixed(2)}</div></div>
                  <div className="stat-box">
                    <div className="label">Balanced</div>
                    <div className="value" style={{ color: balanced ? '#10b981' : '#ef4444' }}>{balanced ? 'Yes ✓' : 'No ✗'}</div>
                  </div>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Code</th><th>Account</th><th>Debit</th><th>Credit</th><th>Balance</th></tr></thead>
                    <tbody>
                      {activeRows.length === 0 ? (
                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No posted activity yet</td></tr>
                      ) : activeRows.map((r, idx) => (
                        <tr key={idx}>
                          <td style={{ fontFamily: 'monospace' }}>{r.accountCode}</td>
                          <td>{r.accountName}</td>
                          <td>{Number(r.totalDebit) > 0 ? Number(r.totalDebit).toFixed(2) : ''}</td>
                          <td>{Number(r.totalCredit) > 0 ? Number(r.totalCredit).toFixed(2) : ''}</td>
                          <td style={{ fontWeight: 600 }}>{Number(r.balance).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </section>
      )}
    </motion.div>
  );
};

export default Accounting;